/**
 * [AAIA] Custom Header Web Component
 * Handles mobile menu drawer, megamenu interactions, standard dropdowns,
 * configurable hover/click trigger, promo carousel, and sticky header behavior.
 */

if (!customElements.get('custom-header')) {
    class CustomHeader extends HTMLElement {
        constructor() {
            super();

            // State
            this.isMenuOpen = false;
            this.activeMegamenu = null;
            this.activeSubmenu = null;
            this.activeDropdown = null;
            this.hoverTimeout = null;
            this.dropdownHoverTimeout = null;

            // Breakpoint
            this.desktopBreakpoint = 1024;

            // Bound methods for event removal
            this.handleResize = this.handleResize.bind(this);
            this.handleKeyDown = this.handleKeyDown.bind(this);
            this.handleClickOutside = this.handleClickOutside.bind(this);
        }

        connectedCallback() {
            this.init();
        }

        disconnectedCallback() {
            this.removeEventListeners();
            document.body.classList.remove('has-sticky-header');
        }

        init() {
            // Determine trigger mode
            this.openTrigger = this.getAttribute('data-open-trigger') || 'hover';
            this.isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

            // Cache DOM elements
            this.cacheElements();

            // Set up event listeners
            this.setupEventListeners();

            // Initialize sticky header class on body
            this.initStickyHeader();

            // Initialize header height for sticky
            this.updateHeaderHeight();

            // Initialize promo carousels
            this.initPromoCarousels();

            // Check initial viewport
            this.handleResize();
        }

        initStickyHeader() {
            if (this.hasAttribute('data-sticky')) {
                document.body.classList.add('has-sticky-header');
            }
        }

        cacheElements() {
            // Mobile elements
            this.menuToggle = this.querySelector('[data-menu-toggle]');
            this.menuDrawer = this.querySelector('[data-menu-drawer]');
            this.backdrop = this.querySelector('[data-backdrop]');

            // Desktop megamenu elements
            this.megamenuTriggers = this.querySelectorAll('[data-megamenu-trigger]');
            this.megamenus = this.querySelectorAll('[data-megamenu]');

            // Desktop dropdown elements
            this.dropdownTriggers = this.querySelectorAll('[data-dropdown-trigger]');

            // Mobile submenu elements
            this.submenuToggles = this.querySelectorAll('[data-submenu-toggle]');
            this.submenus = this.querySelectorAll('[data-submenu]');
            this.submenuBacks = this.querySelectorAll('[data-submenu-back]');
        }

        setupEventListeners() {
            // Mobile menu toggle
            this.menuToggle?.addEventListener('click', () => this.toggleMobileMenu());

            // Backdrop click to close
            this.backdrop?.addEventListener('click', () => this.closeMobileMenu());

            // Click on ::before backdrop pseudo-element
            this.addEventListener('click', (e) => {
                if (this.isMenuOpen && e.target === this) {
                    this.closeMobileMenu();
                }
            });

            // Mobile submenu toggles
            this.submenuToggles.forEach(toggle => {
                toggle.addEventListener('click', () => {
                    const submenuId = toggle.getAttribute('data-submenu-toggle');
                    this.openSubmenu(submenuId);
                });
            });

            // Mobile submenu back buttons
            this.submenuBacks.forEach(back => {
                back.addEventListener('click', () => {
                    const submenuId = back.getAttribute('data-submenu-back');
                    this.closeSubmenu(submenuId);
                });
            });

            // Desktop megamenu triggers
            this.megamenuTriggers.forEach(trigger => {
                const megamenuId = trigger.getAttribute('aria-controls');
                const megamenu = document.getElementById(megamenuId);
                if (!megamenu) return;

                this.setupDesktopTrigger(trigger, megamenu, 'megamenu');
            });

            // Desktop dropdown triggers
            this.dropdownTriggers.forEach(trigger => {
                const navItem = trigger.closest('.custom-header__nav-item');
                const dropdown = navItem?.querySelector(':scope > [data-dropdown]');
                if (!dropdown) return;

                this.setupDesktopTrigger(trigger, dropdown, 'dropdown');

                // Setup nested dropdowns within this dropdown
                this.setupNestedDropdowns(dropdown);
            });

            // Global event listeners
            window.addEventListener('resize', this.handleResize);
            document.addEventListener('keydown', this.handleKeyDown);
            document.addEventListener('click', this.handleClickOutside);
        }

        /**
         * Sets up hover and click behavior for a desktop trigger (megamenu or dropdown).
         * Respects the configurable trigger mode (hover/click).
         */
        setupDesktopTrigger(trigger, panel, type) {
            const useHover = this.openTrigger === 'hover' && !this.isTouch;

            if (useHover) {
                // Hover: mouseenter opens, mouseleave closes with delay
                trigger.addEventListener('mouseenter', () => {
                    if (!this.isDesktop()) return;
                    if (type === 'megamenu') {
                        this.openMegamenu(trigger, panel);
                    } else {
                        this.openDropdown(trigger, panel);
                    }
                });

                trigger.addEventListener('mouseleave', () => {
                    if (!this.isDesktop()) return;
                    if (type === 'megamenu') {
                        this.scheduleMegamenuClose(trigger, panel);
                    } else {
                        this.scheduleDropdownClose(trigger, panel);
                    }
                });

                // Keep open when hovering over the panel
                panel.addEventListener('mouseenter', () => {
                    if (!this.isDesktop()) return;
                    if (type === 'megamenu') {
                        this.cancelMegamenuClose();
                    } else {
                        this.cancelDropdownClose();
                    }
                });

                panel.addEventListener('mouseleave', () => {
                    if (!this.isDesktop()) return;
                    if (type === 'megamenu') {
                        this.closeMegamenu(trigger, panel);
                    } else {
                        this.closeDropdown(trigger, panel);
                    }
                });
            }

            // Click: always available (accessibility, and primary mode if click-trigger)
            trigger.addEventListener('click', (e) => {
                if (!this.isDesktop()) return;
                e.preventDefault();

                if (type === 'megamenu') {
                    const isOpen = trigger.getAttribute('aria-expanded') === 'true';
                    if (isOpen) {
                        this.closeMegamenu(trigger, panel);
                    } else {
                        this.closeAllMegamenus();
                        this.closeAllDropdowns();
                        this.openMegamenu(trigger, panel);
                    }
                } else {
                    const isOpen = trigger.getAttribute('aria-expanded') === 'true';
                    if (isOpen) {
                        this.closeDropdown(trigger, panel);
                    } else {
                        this.closeAllDropdowns();
                        this.closeAllMegamenus();
                        this.openDropdown(trigger, panel);
                    }
                }
            });
        }

        /**
         * Sets up nested dropdown behavior within a parent dropdown.
         * Nested dropdowns always use hover, regardless of the top-level trigger mode.
         */
        setupNestedDropdowns(parentDropdown) {
            const parentItems = parentDropdown.querySelectorAll('.custom-header__dropdown-item--has-children');

            parentItems.forEach(item => {
                const parentLink = item.querySelector('[data-dropdown-parent]');
                const nestedDropdown = item.querySelector('[data-dropdown-nested]');
                if (!parentLink || !nestedDropdown) return;

                let nestedTimeout = null;

                item.addEventListener('mouseenter', () => {
                    if (!this.isDesktop()) return;
                    clearTimeout(nestedTimeout);
                    // Close other nested dropdowns in the same parent
                    parentDropdown.querySelectorAll('.custom-header__dropdown--nested.is-active').forEach(d => {
                        if (d !== nestedDropdown) {
                            d.classList.remove('is-active');
                            d.setAttribute('aria-hidden', 'true');
                        }
                    });
                    nestedDropdown.classList.add('is-active');
                    nestedDropdown.setAttribute('aria-hidden', 'false');
                });

                item.addEventListener('mouseleave', () => {
                    if (!this.isDesktop()) return;
                    nestedTimeout = setTimeout(() => {
                        nestedDropdown.classList.remove('is-active');
                        nestedDropdown.setAttribute('aria-hidden', 'true');
                    }, 100);
                });
            });
        }

        removeEventListeners() {
            window.removeEventListener('resize', this.handleResize);
            document.removeEventListener('keydown', this.handleKeyDown);
            document.removeEventListener('click', this.handleClickOutside);
        }

        // ============================================
        // Mobile Menu Methods
        // ============================================

        toggleMobileMenu() {
            if (this.isMenuOpen) {
                this.closeMobileMenu();
            } else {
                this.openMobileMenu();
            }
        }

        openMobileMenu() {
            this.isMenuOpen = true;
            this.classList.add('menu-open');
            document.body.classList.add('menu-drawer-open');

            this.menuToggle?.setAttribute('aria-expanded', 'true');
            this.menuDrawer?.setAttribute('aria-hidden', 'false');

            const firstFocusable = this.menuDrawer?.querySelector('input, button, a');
            firstFocusable?.focus();
        }

        closeMobileMenu() {
            this.isMenuOpen = false;
            this.classList.remove('menu-open');
            document.body.classList.remove('menu-drawer-open');

            this.menuToggle?.setAttribute('aria-expanded', 'false');
            this.menuDrawer?.setAttribute('aria-hidden', 'true');

            this.closeAllSubmenus();
            this.menuToggle?.focus();
        }

        // ============================================
        // Mobile Submenu Methods
        // ============================================

        openSubmenu(submenuId) {
            const submenu = this.querySelector(`[data-submenu="${submenuId}"]`);
            const toggle = this.querySelector(`[data-submenu-toggle="${submenuId}"]`);
            if (!submenu) return;

            this.activeSubmenu = submenuId;
            submenu.classList.add('is-active');
            submenu.setAttribute('aria-hidden', 'false');
            toggle?.setAttribute('aria-expanded', 'true');

            const backButton = submenu.querySelector('[data-submenu-back]');
            backButton?.focus();
        }

        closeSubmenu(submenuId) {
            const submenu = this.querySelector(`[data-submenu="${submenuId}"]`);
            const toggle = this.querySelector(`[data-submenu-toggle="${submenuId}"]`);
            if (!submenu) return;

            this.activeSubmenu = null;
            submenu.classList.remove('is-active');
            submenu.setAttribute('aria-hidden', 'true');
            toggle?.setAttribute('aria-expanded', 'false');
            toggle?.focus();
        }

        closeAllSubmenus() {
            this.submenus.forEach(submenu => {
                submenu.classList.remove('is-active');
                submenu.setAttribute('aria-hidden', 'true');
            });
            this.submenuToggles.forEach(toggle => {
                toggle.setAttribute('aria-expanded', 'false');
            });
            this.activeSubmenu = null;
        }

        // ============================================
        // Desktop Megamenu Methods
        // ============================================

        openMegamenu(trigger, megamenu) {
            this.cancelMegamenuClose();
            this.closeAllMegamenus();
            this.closeAllDropdowns();

            this.activeMegamenu = megamenu;
            trigger.setAttribute('aria-expanded', 'true');
            trigger.classList.add('custom-header__nav-link--active');
            megamenu.classList.add('is-active');
            megamenu.setAttribute('aria-hidden', 'false');
        }

        closeMegamenu(trigger, megamenu) {
            if (!megamenu) return;
            this.activeMegamenu = null;
            trigger?.setAttribute('aria-expanded', 'false');
            trigger?.classList.remove('custom-header__nav-link--active');
            megamenu.classList.remove('is-active');
            megamenu.setAttribute('aria-hidden', 'true');
        }

        scheduleMegamenuClose(trigger, megamenu) {
            this.hoverTimeout = setTimeout(() => {
                this.closeMegamenu(trigger, megamenu);
            }, 150);
        }

        cancelMegamenuClose() {
            if (this.hoverTimeout) {
                clearTimeout(this.hoverTimeout);
                this.hoverTimeout = null;
            }
        }

        closeAllMegamenus() {
            this.megamenuTriggers.forEach(trigger => {
                const megamenuId = trigger.getAttribute('aria-controls');
                const megamenu = document.getElementById(megamenuId);
                if (megamenu) {
                    this.closeMegamenu(trigger, megamenu);
                }
            });
        }

        // ============================================
        // Desktop Dropdown Methods
        // ============================================

        openDropdown(trigger, dropdown) {
            this.cancelDropdownClose();
            this.closeAllDropdowns();
            this.closeAllMegamenus();

            this.activeDropdown = dropdown;
            trigger.setAttribute('aria-expanded', 'true');
            trigger.classList.add('custom-header__nav-link--active');
            dropdown.classList.add('is-active');
            dropdown.setAttribute('aria-hidden', 'false');
        }

        closeDropdown(trigger, dropdown) {
            if (!dropdown) return;
            this.activeDropdown = null;
            trigger?.setAttribute('aria-expanded', 'false');
            trigger?.classList.remove('custom-header__nav-link--active');
            dropdown.classList.remove('is-active');
            dropdown.setAttribute('aria-hidden', 'true');

            // Also close nested dropdowns
            dropdown.querySelectorAll('.custom-header__dropdown--nested.is-active').forEach(nested => {
                nested.classList.remove('is-active');
                nested.setAttribute('aria-hidden', 'true');
            });
        }

        scheduleDropdownClose(trigger, dropdown) {
            this.dropdownHoverTimeout = setTimeout(() => {
                this.closeDropdown(trigger, dropdown);
            }, 150);
        }

        cancelDropdownClose() {
            if (this.dropdownHoverTimeout) {
                clearTimeout(this.dropdownHoverTimeout);
                this.dropdownHoverTimeout = null;
            }
        }

        closeAllDropdowns() {
            this.dropdownTriggers.forEach(trigger => {
                const navItem = trigger.closest('.custom-header__nav-item');
                const dropdown = navItem?.querySelector(':scope > [data-dropdown]');
                if (dropdown) {
                    this.closeDropdown(trigger, dropdown);
                }
            });
        }

        // ============================================
        // Promo Carousel
        // ============================================

        initPromoCarousels() {
            const carousels = this.querySelectorAll('[data-promo-layout="carousel"]');

            carousels.forEach(carousel => {
                const items = carousel.querySelectorAll('[data-promo-item]');
                if (items.length <= 1) return;

                let currentIndex = 0;

                const showItem = (index) => {
                    items.forEach((item, i) => {
                        if (i === index) {
                            item.classList.remove('custom-header__promo-card--hidden');
                        } else {
                            item.classList.add('custom-header__promo-card--hidden');
                        }
                    });
                    currentIndex = index;
                };

                const prevBtn = carousel.querySelector('[data-promo-carousel-prev]');
                const nextBtn = carousel.querySelector('[data-promo-carousel-next]');

                prevBtn?.addEventListener('click', (e) => {
                    e.stopPropagation();
                    const newIndex = (currentIndex - 1 + items.length) % items.length;
                    showItem(newIndex);
                });

                nextBtn?.addEventListener('click', (e) => {
                    e.stopPropagation();
                    const newIndex = (currentIndex + 1) % items.length;
                    showItem(newIndex);
                });

                // Ensure first item is visible
                showItem(0);
            });
        }

        // ============================================
        // Utility Methods
        // ============================================

        isDesktop() {
            return window.innerWidth >= this.desktopBreakpoint;
        }

        handleResize() {
            if (this.isDesktop() && this.isMenuOpen) {
                this.closeMobileMenu();
            }

            if (!this.isDesktop()) {
                this.closeAllMegamenus();
                this.closeAllDropdowns();
            }

            this.updateHeaderHeight();
        }

        handleKeyDown(event) {
            if (event.key === 'Escape') {
                if (this.isMenuOpen) {
                    if (this.activeSubmenu) {
                        this.closeSubmenu(this.activeSubmenu);
                    } else {
                        this.closeMobileMenu();
                    }
                }

                if (this.activeMegamenu) {
                    this.closeAllMegamenus();
                }

                if (this.activeDropdown) {
                    this.closeAllDropdowns();
                }
            }
        }

        handleClickOutside(event) {
            if (!this.isDesktop()) return;

            // Close megamenu if clicking outside
            if (this.activeMegamenu) {
                const isInsideMegamenu = this.activeMegamenu.contains(event.target);
                const isInsideTrigger = Array.from(this.megamenuTriggers).some(t => t.contains(event.target));

                if (!isInsideMegamenu && !isInsideTrigger) {
                    this.closeAllMegamenus();
                }
            }

            // Close dropdown if clicking outside
            if (this.activeDropdown) {
                const isInsideDropdown = this.activeDropdown.contains(event.target);
                const isInsideTrigger = Array.from(this.dropdownTriggers).some(t => t.contains(event.target));

                if (!isInsideDropdown && !isInsideTrigger) {
                    this.closeAllDropdowns();
                }
            }
        }

        updateHeaderHeight() {
            const mobileBar = this.querySelector('.custom-header__mobile-bar');
            const topBar = this.querySelector('.custom-header__top-bar');
            const navBar = this.querySelector('.custom-header__nav-bar');

            let height = 0;

            if (this.isDesktop()) {
                if (topBar) height += topBar.offsetHeight;
                if (navBar) height += navBar.offsetHeight;
            } else {
                if (mobileBar) height = mobileBar.offsetHeight;
            }

            document.documentElement.style.setProperty('--header-height', `${height}px`);

            // Update drawer top position on mobile
            if (!this.isDesktop() && this.menuDrawer && mobileBar) {
                this.menuDrawer.style.top = `${mobileBar.offsetHeight}px`;
                this.menuDrawer.style.height = `calc(100vh - ${mobileBar.offsetHeight}px)`;

                if (this.backdrop) {
                    this.backdrop.style.top = `${mobileBar.offsetHeight}px`;
                }
            }
        }
    }

    customElements.define('custom-header', CustomHeader);
}

/**
 * Cart count update listener
 * Updates cart count badges when cart is modified
 */
document.addEventListener('DOMContentLoaded', () => {
    const updateCartCounts = (count) => {
        const badges = document.querySelectorAll('.custom-header__cart-count');
        badges.forEach(badge => {
            if (count > 0 && count < 100) {
                badge.textContent = count;
                badge.style.display = '';
            } else if (count >= 100) {
                badge.textContent = '';
                badge.style.display = '';
            } else {
                badge.style.display = 'none';
            }
        });
    };

    const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            if (mutation.type === 'childList' || mutation.type === 'characterData') {
                const cartBubble = document.querySelector('#cart-icon-bubble .cart-count-bubble span[aria-hidden]');
                if (cartBubble) {
                    const count = parseInt(cartBubble.textContent, 10) || 0;
                    updateCartCounts(count);
                } else {
                    const hasBubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
                    updateCartCounts(hasBubble ? 100 : 0);
                }
                break;
            }
        }
    });

    const cartIconBubble = document.querySelector('#cart-icon-bubble');
    if (cartIconBubble) {
        observer.observe(cartIconBubble, {
            childList: true,
            subtree: true,
            characterData: true
        });
    }
});
